import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import {
  DataEntryDetailsComponent,
  FormActionButtonsComponent,
  KpiForecastedValuesComponent,
  MasterForecastedValuesComponent,
} from "../../../../shared/components";
import { Constants } from "../../../../shared/constants/constants";
import { DataEntryDetails } from "../../../../shared/models";
import { ForecastedValuesApiService, SystemLayoutService } from "../../../../shared/services";

@Component({
  selector: "adaa-forecasted-values-kpis-editor",
  imports: [
    TranslatePipe,
    DataEntryDetailsComponent,
    KpiForecastedValuesComponent,
    MasterForecastedValuesComponent,
    FormActionButtonsComponent,
  ],
  templateUrl: "./forecasted-values-kpis-editor.html",
  styleUrl: "./forecasted-values-kpis-editor.scss",
})
export class ForecastedValuesKpisEditorComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _forecastedValuesApiService = inject(ForecastedValuesApiService);
  private readonly _systemLayoutService = inject(SystemLayoutService);

  id = input.required<string>();

  private readonly _untilDestroy = AdaaHelper.untilDestroyed();

  model = signal<DataEntryDetails>({ kpiId: 0 });
  modelToSave = signal<DataEntryDetails>({ kpiId: 0 });

  isFormula = computed<boolean>(() => this.model().formula !== Constants.FORMULA_STRING.NOFORMULA);
  isMasterKPI = computed<boolean>(
    () => this.model().entityValues != undefined && this.model().entityValues!.length > 0
  );

  public ngOnInit(): void {
    this._initPage();

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => this.goBack(),
    });

    this._systemLayoutService.hasCycleChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => this.goBack(),
    });
  }

  private _initPage(): void {
    this._forecastedValuesApiService.getByKpiId(+this.id()).subscribe({
      next: (response) => {
        if (response.inError) {
          this.goBack();
          return;
        }
        this.model.set(response.responseData);
      },
    });
  }

  public goBack(): void {
    this._router.navigateByUrl("/console/forecasted-values/kpis");
  }

  public valueChanged(value: DataEntryDetails): void {
    this.modelToSave.set(value);
  }

  public submit(): void {
    if (this.modelToSave().kpiId == 0) {
      this._toastrService.warning(this._translateService.instant("notification.warning.no_data_to_modify"));
      return;
    }

    const hasErrors =
      this.isFormula() && !this.isMasterKPI()
        ? this.modelToSave().values?.some((e) => e.metrics?.some((m) => m.invalid))
        : this.modelToSave().values?.some((e) => e.invalid);

    if (hasErrors) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_data"));
      return;
    }

    if (this.isFormula() && !this.isMasterKPI()) {
      const values = this.modelToSave().values;
      this.modelToSave().values = [];

      values?.forEach((e) => {
        if (e.metrics) this.modelToSave().values?.push(...e.metrics);
      });
    } else if (this.isMasterKPI()) {
      this.modelToSave().values?.forEach((e) => {
        delete e.entityId;
      });
    }

    this._forecastedValuesApiService.updateForecastedValue(this.modelToSave()).subscribe({
      next: (response) => {
        if (response.inError) return;

        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this.goBack();
      },
    });
  }
}
